// @flow
import * as THREE from 'three'
import Stats from 'stats.js'

import { loadShaders, loadImage  } from '../utils/netUtils'
import { floorPowerOfTwo  } from '../utils/mathUtils'

import type CameraApp from '../utils/cameraApp'
import type { DivRefType, MouseType } from 'react-helper'
import type { PositionType } from 'application-helper'
import type { DailyItemType } from 'database-helper'
 
const BLUR_FILTER = 'blur(10px)'

type firstArticleUniforms = {
    u_pic: {
      value: THREE.Texture
    },
    u_mask: {
      value: THREE.Texture
    },
    u_color: {
      value: THREE.Vector3
    },
    u_ratio: {
      value: number
    },
    u_time: {
      value: number
    },
    u_time_max: {
      value: number
    },
    u_movement: {
      value: number
    },
  }

export default class ThreeApp {
  FOV: number
  CAM_AT: number
  ITERATIONS: number
  CAMERA_STEP: number
  OVERVIEW_HEIGHT_STEP: number
  container: ?HTMLDivElement
  vFOV: number
  fullScreenHeight: number
  fullScreenWidth: ?number
  formattedHeight: ?number
  formattedWidth: number
  firstArticleUniforms: ?firstArticleUniforms
  shaderPlane: ?THREE.PlaneGeometry
  mouse: MouseType
  isClicked: boolean
  dragged: boolean
  draggedPosition: {}
  isReveal: boolean
  mouseFirstPlane: boolean
  shaderPlaneName: string
  navbarView: boolean
  referenceItemPos: PositionType

  bluredCanvas: ?HTMLCanvasElement
  bluredTexture: ?THREE.Texture
  bluredImage: ?HTMLImageElement

  /*
  **  Every items are separated by  ___ this.getGap() ____
  */

  constructor() {
    // @TODO pass container here
    this.FOV = 75
    this.CAM_AT = 40
    this.ITERATIONS = 20
    this.CAMERA_STEP = this.CAM_AT / this.ITERATIONS
    this.OVERVIEW_HEIGHT_STEP = 5
    this.container = undefined
    this.vFOV = THREE.Math.degToRad( this.FOV ); // convert vertical fov to radians
    this.fullScreenHeight = 2 * Math.tan( this.vFOV / 2 ) * this.CAM_AT
    this.fullScreenWidth = undefined
    this.formattedHeight = undefined
    this.formattedWidth = this.getWidthFromHeight(this.fullScreenHeight)
    this.firstArticleUniforms = undefined
    this.shaderPlane = undefined
    this.navbarView = false
    this.referenceItemPos = { x: 0, y: 0, z: 0 }

    // First plane events
    this.mouse = { x: 0, y: 0 } // xy mouse
    this.isClicked = false // if window is clicked
    this.dragged = false
    this.draggedPosition = {x: 0}
    this.mouseFirstPlane = false
    this.isReveal = false
    this.shaderPlaneName = 'shaderPlane' // Save name of the first plane
    this.bluredCanvas = undefined
    this.bluredTexture = undefined
    this.bluredImage = undefined
  }

  getXYZPos = (index: number, overviewItemIndex: number, height: number): PositionType => {
    const x = 0
    const y = this.getUpdatedYPos(index, overviewItemIndex, height)
    const z = this.getUpdatedZPos(index, overviewItemIndex)
    return {x, y, z}
  }

  getPlanePos = (name: string, scene: THREE.Scene): PositionType => {
    const plane = scene.getObjectByName(name)
    if (plane) return plane.position
    return { x: NaN, y: NaN, z: NaN }
  }

  getGap = (): number => this.navbarView ? this.CAM_AT * 0.5 : this.CAM_AT * 5

  getYGap = (height: number): number => height / this.OVERVIEW_HEIGHT_STEP

  getDailyItemIndex = (cameraZPos: number, overviewItemIndex: number, overviewItem: DailyItemType, scene: THREE.Scene): number => {
    const overviewItemPos = this.getUpdatedZPos(overviewItemIndex, overviewItemIndex)
    const index = Math.floor((cameraZPos - overviewItemPos) / this.getGap()) + overviewItemIndex
    return index
  }

  getZPos(index: number): number {
    const zPos = index * this.getGap()
    return zPos
  }

  getUpdatedYPos(index: number, overviewItemIndex: number, height: number): number {
    if (this.navbarView) return this.getOverviewY(index, overviewItemIndex, height)
    const referenceItemPos = this.referenceItemPos
    if (Number.isNaN(referenceItemPos.y)) return 0
    return referenceItemPos.y
  }

  getUpdatedZPos(index: number, overviewItemIndex: number): number {
    if (this.navbarView) return this.getOverviewZ(index, overviewItemIndex)
    const referenceItemPos = this.referenceItemPos
    if (Number.isNaN(referenceItemPos.z) || overviewItemIndex === -1) return this.getZPos(index)
    return referenceItemPos.z + (this.getGap() * (index - overviewItemIndex))
  }

  getZYPos(index: number, dailyItemIndex: ?number, height: ?number): {z: number, y: number} {
    const z = this.getZPos(index)
    const y = 0
    return {z, y}
  }

  getOverviewY(index: number, overviewItemIndex: number, height: number): number {
    return this.getYGap(height) * (overviewItemIndex - index)
  }

  getOverviewZ(index: number, overviewItemIndex: number): number {
    return this.getGap() * (index - overviewItemIndex)
  }

  setReferenceItemPos(pos: PositionType) { this.referenceItemPos = pos }

  getReferenceItemPos(): PositionType { return this.referenceItemPos }

  getHeightFromWidth(width: number): number {
    // return width / 1.78 // 16:9
    return width / 1.6 // 16:10
  }

  getWidthFromHeight(height: number): number {
    return height * 1.6
  }

  setNavbarView (navbarView: boolean) { this.navbarView = navbarView }

  setContainer(rendererContainerRef: DivRefType) {
    if (rendererContainerRef.current) {
      this.container = rendererContainerRef.current
    }
  }

  setFirstArticleUniforms = function(uniforms: firstArticleUniforms) {
    this.firstArticleUniforms = uniforms
  }

  createCamera(lastArticleIndex: number): THREE.PerspectiveCamera {
    const container = this.container
    if (container) {
      const width = container.clientWidth
      const height = container.clientHeight
      return new THREE.PerspectiveCamera( this.FOV, width / height, 0.1, this.getZPos(lastArticleIndex) )
    }
  }

  setRendererSize(renderer: THREE.WebGLRenderer) {
    const container = this.container
    if (container) {
      renderer.setSize(container.clientWidth, container.clientHeight)
      this.fullScreenWidth = this.fullScreenHeight * (container.clientWidth / container.clientHeight); // visible width
      this.formattedHeight = this.getHeightFromWidth(this.fullScreenWidth)
    }
  }

  appendRenderer(renderer: THREE.WebGLRenderer) {
    const container = this.container
    if (container) {
      container.appendChild(renderer.domElement)
    }
  }

  getScale(width: number, height: number): Array<number> {
    const ratio = width / height
    const returnValue = 1.6 / ratio
    return [returnValue, returnValue, returnValue]
  }

  createFullScreenGeometry = (): THREE.PlaneGeometry => {
    // @SEE https://stackoverflow.com/questions/13350875/three-js-width-of-view/13351534#13351534
    const container = this.container
    const containerWidth = container ? container.clientWidth : 1280
    const containerHeight = container ? container.clientHeight : 800

    const fullScreenWidth = this.fullScreenWidth || this.fullScreenHeight * (containerWidth / containerHeight);  // visible width
    const formattedHeight = this.getHeightFromWidth(fullScreenWidth)
    const geometry = new THREE.PlaneGeometry(fullScreenWidth, formattedHeight)
    if (containerWidth / containerHeight < 1.6) geometry.scale(...this.getScale(containerWidth, containerHeight))
    return geometry
  }

  createArticlePlane(name: ?string, index: number, texture: THREE.Texture): THREE.Mesh {
    const geometry = this.createFullScreenGeometry()
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.repeat.set(1, 1)
    const material = new THREE.MeshBasicMaterial({
      map: texture
    })
    const plane = new THREE.Mesh( geometry, material )
    plane.name = name || ''
    // plane.position.setZ(this.getZPos(index))
    plane.position.setZ(this.getUpdatedZPos(index, -1))
    return plane
  }

  createShaderArticlePlane = function(name: ?string, index: number, texture: THREE.Texture, alphaTexture: THREE.Texture, scene: THREE.Scene): Promise<THREE.Mesh> {
    return new Promise((resolve, reject) => {
      this.shaderPlaneName = name || 'shaderPlane'
      const uniforms = this.firstArticleUniforms || {}
      const geometry = this.createFullScreenGeometry()

      loadShaders('/shaders/Article/fragment_distort.frag', '/shaders/Article/vertex_distort.vert')
        .then(res => {
          const fragShader = res.fragment;
          const vertShader = res.vertex;

          uniforms.mouse = this.mouse // @REFACTO --> Useful ?

          const shaderMaterial = new THREE.ShaderMaterial( {
            uniforms: uniforms,
            vertexShader: vertShader,
            fragmentShader: fragShader,
          });

          const plane = new THREE.Mesh( geometry, shaderMaterial )
          // plane.position.setZ(this.getZPos(index))
          plane.position.setZ(this.getUpdatedZPos(index, -1))
          plane.name = this.shaderPlaneName || 'shaderPlane'
          plane.material.transparent = true
          this.shaderPlane = plane
          resolve(plane)
        })
        .catch(reject)
    })
  }

  /**** CAMERA ****/

  setCameraZPos(camera: THREE.PerspectiveCamera, pos: number): THREE.PerspectiveCamera {
    const newCam = camera.clone()
    newCam.position.z = pos
    return newCam
  }

  setCameraZYPos(camera: THREE.PerspectiveCamera, pos: {z: number, y: number}): THREE.PerspectiveCamera {
    const newCam = camera.clone()
    newCam.position.z = pos.z
    newCam.position.y = pos.y
    return newCam
  }

  setBaseCameraPosition = function(camera: THREE.PerspectiveCamera, entries: number): THREE.PerspectiveCamera {
    const pos = this.getUpdatedZPos(entries + 1, entries)
    return this.setCameraZPos(camera, pos)
  }

  setCameraForFocus = function(objectZPos: number, camera: THREE.PerspectiveCamera): THREE.PerspectiveCamera {
    const fullWidthPos = objectZPos + this.CAM_AT
    return this.setCameraZPos(camera, fullWidthPos)
  }

  static createPowerOfTwoCanvas = function(url: string, crossOrigin: boolean): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {
      loadImage(url, crossOrigin)
        .then((img: HTMLImageElement) => {
          const canvas = document.createElement('canvas')
          canvas.width = floorPowerOfTwo(img.width)
          canvas.height = floorPowerOfTwo(img.height)

          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          resolve(canvas)
        })
        .catch(reject)
    })
  }

  static loadPowerOfTwoTexture = function(url: string, crossOrigin: boolean): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      ThreeApp.createPowerOfTwoCanvas(url, crossOrigin)
        .then((canvas: HTMLCanvasElement) => {
          const texture = new THREE.Texture(canvas)
          texture.wrapS = THREE.ClampToEdgeWrapping
          texture.wrapT = THREE.ClampToEdgeWrapping
          texture.needsUpdate = true
          resolve(texture)
        })
        .catch(reject)
    })
  }

  static loadTextureAsync = function(url: string): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      const onLoad = (data) => resolve(data)
      const onError = (data) => reject(data)
      new THREE.TextureLoader().load(url, onLoad, undefined, onError)
    })
  }

  loadTextureAsync = ThreeApp.loadTextureAsync

  loadCanvasTexture = function(image: HTMLImageElement): THREE.Texture {
    this.bluredImage = image
    this.bluredCanvas = document.createElement('canvas')          
    const ctx = this.bluredCanvas.getContext('2d')
    this.bluredCanvas.width = floorPowerOfTwo(image.width)
    this.bluredCanvas.height = floorPowerOfTwo(image.height)
    ctx.filter = BLUR_FILTER
    ctx.drawImage(image, 0, 0, this.bluredCanvas.width, this.bluredCanvas.height)
    this.bluredTexture = new THREE.Texture(this.bluredCanvas)
    this.bluredTexture.wrapS = THREE.ClampToEdgeWrapping
    this.bluredTexture.wrapT = THREE.ClampToEdgeWrapping
    this.bluredTexture.needsUpdate = true
    return this.bluredTexture
  }

  unblur = function(uid: string, scene: THREE.Scene) {
    const canvas = this.bluredCanvas
    const image = this.bluredImage
    if (!canvas || !image) return undefined
    const ctx = canvas.getContext('2d')
    if (ctx.filter !== BLUR_FILTER) return undefined
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.filter = 'blur(0px)'
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
    const plane = scene.getObjectByName(uid)
    if (plane) plane.material.map.needsUpdate = true
  }

  setFirstArticleClicked = function() {
    if (!this.isClicked) this.isClicked = true
  }

  static initStats = function(): Stats {
    const stats = new Stats()
    stats.setMode(0)
    stats.domElement.style.position = 'absolute'
    stats.domElement.style.left = ''
    stats.domElement.style.right = '0'
    stats.domElement.style.top = '0px'
    window.document.body.appendChild( stats.domElement )
    return stats
  }

  updateUniforms = function(uniform: firstArticleUniforms, raycaster: THREE.Raycaster, scene: THREE.Scene) {
    uniform.u_time.value += .0005
      const intersects = raycaster.intersectObjects(scene.children);
      if(intersects[0] !== undefined) {
        if(intersects[0].object.name === this.shaderPlaneName && uniform.u_time_max.value === 0) {
          this.mouseFirstPlane = true;
          // if(this.dragged) {  @TODO On drag event
          if(this.isClicked) {
            uniform.u_time = { value: 0. };
            uniform.u_time_max = { value: 8. };
            uniform.u_movement = { value: .002 };
            this.isClicked = false;
          }
        } else {
          this.mouseFirstPlane = false;
          this.isClicked = false;
        }
      } else {
        this.mouseFirstPlane = false;
      }
  }

  // @TODO HERE
  commonTriggerRAF(
    triggerRAF: () => void,
    getRenderer: () => THREE.WebGLRenderer,
    getScene: () => THREE.Scene,
    getCamera: () => THREE.PerspectiveCamera,
    getRaycaster: () => THREE.Raycaster,
    getMouse: () => MouseType,
    updateRafId: (rafID: AnimationFrameID) => void,
    updateIntersects: ?(raycaster: THREE.Raycaster) => void,
    cameraApp: CameraApp
  ){
    const rafID = requestAnimationFrame( () => triggerRAF() );
    const camera = getCamera()
    const raycaster = getRaycaster()
    const scene = getScene()
    getRenderer().render( scene, camera );
    raycaster.setFromCamera( getMouse() , camera )
    this.mouse = getMouse()

    if (updateIntersects) updateIntersects(raycaster)
    updateRafId(rafID)

    if (this.firstArticleUniforms) this.updateUniforms(this.firstArticleUniforms, raycaster, scene)
    cameraApp.updatePos()
  }

  animate = function(
    getRenderer: () => THREE.WebGLRenderer,
    getScene: () => THREE.Scene,
    getCamera: () => THREE.PerspectiveCamera,
    getRaycaster: () => THREE.Raycaster,
    getMouse: () => MouseType,
    updateRafId: (rafID: AnimationFrameID) => void,
    updateIntersects: ?(raycaster: THREE.Raycaster) => void,
    cameraApp: CameraApp
  ) {
    if (process.env.NODE_ENV !== 'production') {
      const stats = ThreeApp.initStats()
      const triggerRAF = () => {
        stats.begin()
        this.commonTriggerRAF(
          triggerRAF,
          getRenderer,
          getScene,
          getCamera,
          getRaycaster,
          getMouse,
          updateRafId,
          updateIntersects,
          cameraApp
        )
        stats.end()
      }
      triggerRAF()
    } else {
      const triggerRAF = () => {
        this.commonTriggerRAF(
          triggerRAF,
          getRenderer,
          getScene,
          getCamera,
          getRaycaster,
          getMouse,
          updateRafId,
          updateIntersects,
          cameraApp
        )
      }
      triggerRAF()   
    }
  }

  setResponsive = (container: HTMLDivElement, camera: THREE.PerspectiveCamera, renderer: THREE.WebGLRenderer) => {
    const rect = container.getBoundingClientRect()
    camera.aspect = rect.width / rect.height
    camera.updateProjectionMatrix()
    renderer.setSize(rect.width, rect.height)
  }

  /* if too laggy, can create smaller canvas and make it scale */
  static createRenderer = (): THREE.WebGLRenderer => {
    const renderer = new THREE.WebGLRenderer({ alpha: true })
    renderer.setPixelRatio(1)
    renderer.setClearColor(0xffffff, 0)
    return renderer
  }

}


// WEBPACK FOOTER //
// ./src/utils/threeApp.js